import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, MapPin, Search, X, Camera, Loader2, Check, Sparkles, Clock, Store, Truck } from "lucide-react";
import dayjs from "dayjs";
import { api, fileUrl } from "@/lib/api";
import { money, fmtSlotDate } from "@/lib/format";
import { effectivePrice, displayName, getItemTag } from "@/lib/catalog";
import { computePricing, TAILORING_CATEGORY, REWARD_CATEGORY, TAILORING_MULT } from "@/lib/pricing";
import { copy } from "@/lib/copy";
import InfoDot from "@/components/common/InfoDot";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/context/AuthContext";
import { useCustomerProfile, useSavedAddresses, ADDRESS_LABELS, saveCustomerProfile } from "@/hooks/useLocal";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip } from "@/components/common/StatusBadge";
import { QtyStepper } from "@/components/common/QtyStepper";
import { cn } from "@/lib/utils";
import TermsBody from "@/components/TermsBody";

const STEP_TITLES = ["Choose services", "How it works", "Pick a time", "Your details", "Review"];
const SPEED_LABELS = { standard: "Standard", priority: "Priority", express: "Express", rapid: "Rapid" };

export default function Booking() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { shop } = useShop();
  const { user, signIn } = useAuth();
  const isSignedIn = !!user;
  const { profile } = useCustomerProfile();
  const { addresses, save: saveAddress } = useSavedAddresses();

  const [step, setStep] = useState(0);
  const [category, setCategory] = useState(searchParams.get("category"));
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState({});
  const [mode, setMode] = useState("pickup");
  const [address, setAddress] = useState("");
  const [location, setLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [deliverySpeed, setDeliverySpeed] = useState("standard");
  const [tailoringSpeed, setTailoringSpeed] = useState("standard");
  const [dateIdx, setDateIdx] = useState(0);
  const [slot, setSlot] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [referralInput, setReferralInput] = useState("");
  const [referral, setReferral] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(false);
  const [redeemPointsAmount, setRedeemPointsAmount] = useState(null); // null = spend the max usable
  const [redeemGamePoints, setRedeemGamePoints] = useState(false);
  const [redeemMilestonePoints, setRedeemMilestonePoints] = useState(false);
  const [useReward, setUseReward] = useState(false);
  const [useFreeWash, setUseFreeWash] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [showCelebrate, setShowCelebrate] = useState(false);
  const campaignChecked = useRef(false);

  const servicesQuery = useQuery({ queryKey: ["services"], queryFn: api.getServices });
  const services = useMemo(() => servicesQuery.data ?? [], [servicesQuery.data]);
  const phoneDigits = String(phone ?? "").replace(/\D/g, "");
  const loyaltyQuery = useQuery({ queryKey: ["loyalty", phoneDigits], queryFn: () => api.getLoyalty(phoneDigits), enabled: phoneDigits.length >= 7 });
  const loyalty = loyaltyQuery.data;
  const earlybirdQuery = useQuery({ queryKey: ["earlybirdToday"], queryFn: api.getEarlybirdToday, refetchInterval: 60000 });

  // Reorder: prefill cart + address from a past order (one-time).
  const reorderId = searchParams.get("reorder");
  const reorderQuery = useQuery({ queryKey: ["reorder", reorderId], queryFn: () => api.getBooking(reorderId), enabled: !!reorderId });
  const prefilled = useRef(false);
  useEffect(() => {
    const src = reorderQuery.data;
    if (!src || services.length === 0 || prefilled.current) return;
    const next = {};
    for (const it of src.items) if (services.some((s) => s.service_id === it.service_id)) next[it.service_id] = it.qty;
    if (Object.keys(next).length === 0) return;
    prefilled.current = true;
    setCart(next);
    setMode(src.mode);
    if (src.mode === "pickup") {
      if (src.address) setAddress(src.address);
      if (src.location) setLocation(src.location);
    }
    toast.success("Items added from your previous order");
  }, [reorderQuery.data, services]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => dayjs().add(i, "day")), []);
  const selectedDate = days[dateIdx]?.format("YYYY-MM-DD") ?? "";
  const slotAvailQuery = useQuery({ queryKey: ["slotAvailability", mode, selectedDate], queryFn: () => api.getSlotAvailability(mode, selectedDate), enabled: !!selectedDate });
  const slotAvailability = useMemo(() => {
    const map = {};
    for (const a of slotAvailQuery.data ?? []) map[a.slot_time] = a;
    return map;
  }, [slotAvailQuery.data]);
  const selectedSlotAvailability = slot ? slotAvailability[slot] : null;

  // Slot robustness (web): pickup always resolves to a valid, non-full slot; drop-off needs none.
  const pickupSlots = shop?.pickup_slots ?? [];
  const availablePickupSlots = pickupSlots.filter((s) => !slotAvailability[s]?.full);
  const hasValidPickupSlot = mode !== "pickup" || (!!slot && pickupSlots.includes(slot) && !slotAvailability[slot]?.full);
  const noPickupSlotsThisDay = mode === "pickup" && !!slotAvailQuery.data && availablePickupSlots.length === 0;

  useEffect(() => {
    if (profile) {
      setName((n) => n || profile.name);
      setPhone((p) => p || profile.phone);
    }
  }, [profile]);
  useEffect(() => {
    if (user) {
      setName((n) => n || user.name);
      setPhone((p) => p || user.phone);
    }
  }, [user]);
  useEffect(() => {
    setPromo(null);
  }, [cart, tailoringSpeed]);
  // Switching fulfilment mode clears a slot that doesn't belong to the new mode (so it can't block).
  useEffect(() => {
    const list = mode === "pickup" ? (shop?.pickup_slots ?? []) : (shop?.dropoff_slots ?? []);
    setSlot((cur) => (cur && !list.includes(cur) ? null : cur));
  }, [mode, shop]);
  // Pickup auto-resolves to a valid, non-full slot: keep a valid choice, else pick the first
  // available on this date; if the date has none, cascade to the next date (except on the manual
  // slot step). Drop-off needs no slot. This removes the "Invalid time slot" dead-end at Confirm.
  useEffect(() => {
    if (mode !== "pickup" || !slotAvailQuery.data) return;
    const list = shop?.pickup_slots ?? [];
    const isFull = (s) => slotAvailability[s]?.full;
    const avail = list.filter((s) => !isFull(s));
    const valid = slot && list.includes(slot) && !isFull(slot);
    if (valid) return;
    if (avail.length > 0) {
      setSlot(avail[0]);
    } else if (step !== 2 && dateIdx < days.length - 1) {
      setDateIdx((i) => i + 1);
    }
  }, [mode, slotAvailQuery.data, slot, slotAvailability, shop, step, dateIdx, days.length]);

  const pricing = computePricing({
    services, shop, cart, mode, location, deliverySpeed, tailoringSpeed, loyalty, promo,
    earlybird: earlybirdQuery.data,
    redeemPoints: isSignedIn && redeemPoints, redeemPointsAmount, redeemGamePoints: isSignedIn && redeemGamePoints, redeemMilestonePoints: isSignedIn && redeemMilestonePoints,
    useReward: isSignedIn && useReward, useFreeWash: isSignedIn && useFreeWash, referral, selectedSlotAvailability,
  });

  const gamePointsReason =
    pricing.subtotal < pricing.gamePointsMinOrder
      ? `Spend at least ${money(pricing.gamePointsMinOrder)} to use game points (you're at ${money(pricing.subtotal)}).`
      : pricing.gamePointsCapAmt <= 0
        ? "Game points can't be applied to the items in this cart."
        : copy(shop, "copy_game_points_guidance", "Earn more in the Games hub to redeem here.");

  const categories = shop?.categories ?? [];
  const slots = (mode === "pickup" ? shop?.pickup_slots : shop?.dropoff_slots) ?? [];

  const speedFee = (sp) => {
    if (sp === "standard" || pricing.dryWashSubtotal <= 0 || !shop) return 0;
    const feeKey = { priority: "priority_fee", express: "express_fee", rapid: "rapid_fee" }[sp];
    const amtKey = { priority: "priority_per_amount", express: "express_per_amount", rapid: "rapid_per_amount" }[sp];
    return Number(shop[feeKey] || 0) * Math.max(1, Math.ceil(pricing.dryWashSubtotal / (Number(shop[amtKey]) || 500)));
  };
  const speedTurnaround = (sp) =>
    sp === "standard" ? `Standard · T + ${shop?.standard_days ?? 4} days` : sp === "priority" ? "Priority · ready in 48 hours" : sp === "express" ? "Express · ready in 36 hours" : "Rapid · ready in 24 hours";
  const visible = category
    ? services.filter((s) => s.category === category && (!search.trim() || s.name.toLowerCase().includes(search.trim().toLowerCase())))
    : [];
  const categoryCount = (c) => services.reduce((n, s) => n + (s.category === c ? cart[s.service_id] ?? 0 : 0), 0);

  const setQty = (id, qty) => setCart((c) => ({ ...c, [id]: Math.max(0, qty) }));

  // ---- campaign popup: check when entering review ----
  useEffect(() => {
    if (step === 4 && !campaignChecked.current && phoneDigits.length >= 7 && pricing.subtotal > 0) {
      campaignChecked.current = true;
      api
        .campaignOffer({ phone: phoneDigits, subtotal: pricing.subtotal, categories: Object.keys(pricing.categorySubtotals) })
        .then((res) => {
          if (res?.eligible) setCampaign(res);
        })
        .catch(() => {});
    }
  }, [step, phoneDigits, pricing.subtotal, pricing.categorySubtotals]);

  // ---- celebrate on any applied discount in review ----
  const totalDiscount =
    pricing.promoDiscount + pricing.earlybirdDiscount + pricing.pointsDiscount + pricing.gamePointsDiscount +
    pricing.milestonePointsDiscount + pricing.rewardDiscount + pricing.freeWashDiscount + pricing.welcomeDiscount;

  const applyPromo = useMutation({
    mutationFn: () => api.validatePromo(promoInput.trim(), phoneDigits, pricing.subtotal),
    onSuccess: (p) => {
      setPromo(p);
      triggerCelebrate();
      toast.success(`${p.code} applied · ${p.label}`);
    },
    onError: (e) => toast.error(e.message || "Invalid promo code"),
  });
  const applyReferral = useMutation({
    mutationFn: () => api.validateReferral(referralInput.trim().toUpperCase(), phoneDigits),
    onSuccess: (r) => {
      setReferral(r);
      triggerCelebrate();
      toast.success(`Referral from ${r.referrer_name} applied · ${r.label}`);
    },
    onError: (e) => toast.error(e.message || "Invalid referral code"),
  });

  const triggerCelebrate = () => {
    if (!shop?.celebrate_enabled) return;
    setShowCelebrate(true);
    setTimeout(() => setShowCelebrate(false), shop?.celebrate_duration_ms || 1800);
  };

  const onUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= 4) {
      toast.error("You can attach up to 4 photos");
      return;
    }
    setUploadingPhoto(true);
    try {
      const uploaded = await api.uploadPhoto(file);
      setPhotos((p) => [...p, uploaded]);
      toast.success("Photo attached");
    } catch (err) {
      toast.error(err.message || "Could not upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location unavailable — type your address instead");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Location pinned for the pickup rider");
        setLocating(false);
      },
      () => {
        toast.error("Couldn't get your location — type the address instead");
        setLocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  };

  const createBooking = useMutation({
    mutationFn: () =>
      api.createBooking({
        customer_name: name.trim(),
        phone: phoneDigits,
        mode,
        address: mode === "pickup" ? address.trim() : "",
        slot_date: selectedDate,
        slot_time: mode === "pickup" ? (slot ?? "") : "",
        items: Object.entries(cart).filter(([, q]) => q > 0).map(([service_id, qty]) => ({ service_id, qty })),
        notes: notes.trim(),
        promo_code: promo?.code ?? "",
        redeem_points: redeemPoints && pricing.canRedeemPoints,
        redeem_points_amount: redeemPoints && pricing.canRedeemPoints && redeemPointsAmount != null ? redeemPointsAmount : undefined,
        redeem_game_points: redeemGamePoints && pricing.canRedeemGamePoints,
        redeem_milestone_points: redeemMilestonePoints && pricing.canRedeemMilestonePoints,
        use_reward: useReward && pricing.canUseReward,
        use_free_wash: useFreeWash && pricing.canUseFreeWash,
        delivery_speed: deliverySpeed,
        tailoring_speed: tailoringSpeed,
        accepted_terms: acceptedTerms,
        location: mode === "pickup" ? location : null,
        photos: photos.map((p) => p.path),
        referral_code: referral?.code ?? "",
        payment_method: "pickup",
      }),
    onSuccess: (booking) => {
      setConfirmed(booking);
      saveCustomerProfile({ name: name.trim(), phone: phoneDigits });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
    },
    onError: (e) => toast.error(e.message || "Could not create booking"),
  });

  const canContinue =
    step === 0 ? pricing.itemCount > 0
      : step === 1 ? !pricing.shoeBelowMin && (mode === "dropoff" || (address.trim().length >= 10 && location !== null && !pricing.pickupTooFar))
      : step === 2 ? slot !== null
      : step === 3 ? name.trim().length >= 2 && phoneDigits.length >= 7
      : acceptedTerms && !pricing.pickupBelowMin && hasValidPickupSlot;

  const continueStep = () => {
    if (!canContinue) {
      if (step === 0) toast.error("Add at least one item to continue");
      else if (step === 1) {
        if (pricing.shoeBelowMin) toast.error(`Home pickups need ${pricing.shoeMinPairs}+ pairs for shoes${pricing.shoeWaiverValue > 0 ? `, or add other items to pass ${money(pricing.shoeWaiverValue)}` : ""} — or switch to Drop-off`);
        else if (mode === "pickup" && pricing.pickupTooFar) toast.error(`Pickup isn't available this far (over ${pricing.pickupMaxKm} km) — switch to Drop-off`);
        else if (mode === "pickup" && !location) toast.error("Share your location to continue with pickup, or switch to Drop-off");
        else toast.error("Enter your full pickup address");
      } else if (step === 2) {
        if (!noPickupSlotsThisDay) toast.error("Choose a time slot");
      }
      else if (step === 3) toast.error("Enter your name and a valid phone number");
      else if (pricing.pickupBelowMin) toast.error(`Home pickup orders must be at least ${money(pricing.pickupMinOrder)} after discounts — add more items or switch to Drop-off`);
      else toast.error("Please accept the Terms & Care policy");
      return;
    }
    if (step === 4) {
      createBooking.mutate();
      return;
    }
    // Drop-off has no appointment — skip the date/slot step entirely.
    if (step === 1 && mode === "dropoff") {
      setStep(3);
      return;
    }
    setStep(step + 1);
  };

  const goBack = () => {
    if (step === 3 && mode === "dropoff") return setStep(1);
    if (step > 0) return setStep(step - 1);
    navigate(-1);
  };

  const ctaTotal = step === 0 ? pricing.subtotal : pricing.grandTotal;
  const ctaLabel =
    step === 3 ? "Review booking" : step === 4 ? `Confirm · ${money(pricing.grandTotal)} est.` : `Continue · ${money(ctaTotal)}`;

  // ---------------- success ----------------
  if (confirmed) {
    const saved = confirmed.promo_discount + confirmed.points_discount + confirmed.game_points_discount + confirmed.reward_discount + confirmed.welcome_discount + confirmed.free_wash_discount + confirmed.earlybird_discount + confirmed.milestone_points_discount;
    const isPending = confirmed.status === "pending_approval";
    return (
      <div className="mx-auto flex max-w-md flex-col items-center py-10 text-center animate-fade-up">
        <div className={cn("flex h-20 w-20 items-center justify-center rounded-full text-primary-foreground", isPending ? "bg-[hsl(var(--warning))]" : "bg-primary")}>
          {isPending ? <Clock className="h-10 w-10" /> : <Check className="h-10 w-10" />}
        </div>
        <h1 className="mt-5 font-display text-2xl font-bold">{isPending ? "Booking request sent" : "Booking confirmed"}</h1>
        {isPending ? <p className="mt-2 text-sm text-muted-foreground">This early/late pickup time needs the shop&apos;s approval — we&apos;ll confirm shortly.</p> : null}
        <p data-testid="booking-success-code" className="mt-3 font-display text-3xl font-black tracking-wide text-primary">{confirmed.code}</p>
        <div className="mt-5 w-full space-y-2 rounded-2xl bg-secondary p-5 text-left text-sm">
          <p>{fmtSlotDate(confirmed.slot_date)} · {confirmed.slot_time}</p>
          <p>{mode === "pickup" ? "Doorstep pickup & delivery" : "In-store drop-off"}</p>
          <p>{confirmed.items.reduce((a, i) => a + i.qty, 0)} items · {money(confirmed.total)}</p>
        </div>
        <p className="mt-3 rounded-full bg-[hsl(var(--brand-tertiary))] px-4 py-2 text-xs font-medium text-primary">
          Estimated {money(confirmed.total)} · final bill after inspection{saved > 0 ? ` · saved ${money(saved)}` : ""}
        </p>
        <button onClick={() => navigate(`/order/${confirmed.id}`)} data-testid="booking-success-track-button" className="mt-6 w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground">Track order</button>
        <button onClick={() => navigate("/")} data-testid="booking-success-home-button" className="mt-2 w-full rounded-full py-3 font-semibold text-primary">Back to home</button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl pb-32">
      {/* Header */}
      <div className="flex items-center gap-2 py-2">
        <button data-testid="booking-back-button" onClick={goBack} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary">
          <ChevronLeft className="h-6 w-6" />
        </button>
        <h1 className="font-display text-xl font-bold">{STEP_TITLES[step]}</h1>
      </div>
      {/* Progress */}
      <div className="mb-6 flex gap-1.5">
        {STEP_TITLES.map((_, i) => (
          <div key={i} className={cn("h-1.5 flex-1 rounded-full", i <= step ? "bg-primary" : "bg-border")} />
        ))}
      </div>

      {/* STEP 0: catalog */}
      {step === 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <Chip key={c} active={category === c} onClick={() => { setCategory(c); setSearch(""); }} testId={`booking-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {c}{categoryCount(c) > 0 ? ` · ${categoryCount(c)}` : ""}
              </Chip>
            ))}
          </div>
          {!category ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Pick a category above to see items.</p>
          ) : (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search in ${category}`} data-testid="booking-catalog-search" className="w-full rounded-xl border border-border bg-card py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary" />
              </div>
              <div className="space-y-2">
                {visible.map((s) => {
                  const price = effectivePrice(s, shop);
                  const onSale = s.sale_price != null && s.sale_price < s.price;
                  const tag = getItemTag(s);
                  return (
                    <div key={s.service_id} data-testid={`booking-item-${s.service_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{displayName(s)}{tag ? <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{tag}</span> : null}</p>
                        <p className="text-xs text-muted-foreground">
                          {s.unit} · <span className={cn(onSale && "line-through")}>{money(s.price)}</span>
                          {onSale ? <span className="ml-1 font-semibold text-primary">{money(price)}</span> : null}
                        </p>
                        {(cart[s.service_id] ?? 0) > 0 ? (
                          <p className="mt-0.5 text-xs font-semibold text-primary" data-testid={`line-total-${s.service_id}`}>
                            {cart[s.service_id]} × {money(price)} = {money(price * cart[s.service_id])}
                          </p>
                        ) : null}
                      </div>
                      <QtyStepper value={cart[s.service_id] ?? 0} onChange={(q) => setQty(s.service_id, q)} testId={`qty-${s.service_id}`} />
                    </div>
                  );
                })}
                {category === REWARD_CATEGORY ? <p className="px-1 text-xs text-muted-foreground">Per-kg wash is billed by weight (3 kg minimum).</p> : null}
              </div>
            </>
          )}
        </div>
      )}

      {/* STEP 1: mode & speed */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3">
            {[
              { k: "pickup", label: "Doorstep pickup", icon: Truck, desc: "We collect & deliver" },
              { k: "dropoff", label: "In-store drop-off", icon: Store, desc: "Bring it to the shop" },
            ].map((m) => (
              <button key={m.k} data-testid={`booking-mode-${m.k}`} onClick={() => setMode(m.k)} className={cn("rounded-2xl border p-4 text-left transition", mode === m.k ? "border-primary bg-[hsl(var(--brand-tertiary))]" : "border-border bg-card")}>
                <m.icon className="h-5 w-5 text-primary" />
                <p className="mt-2 font-display text-sm font-semibold">{m.label}</p>
                <p className="text-xs text-muted-foreground">{m.desc}</p>
              </button>
            ))}
          </div>

          {mode === "dropoff" && (
            <div className="rounded-2xl bg-[hsl(var(--brand-tertiary))] p-5" data-testid="dropoff-store-hours">
              <p className="font-display text-sm font-bold text-primary">Drop in any time we&apos;re open</p>
              <p className="mt-1 text-sm text-foreground/80">{shop?.address}</p>
              <p className="text-sm text-foreground/80">{shop?.hours}</p>
              <p className="mt-2 text-xs text-muted-foreground">No appointment needed — bring your garments to the store during opening hours. No pickup charge.</p>
            </div>
          )}

          {mode === "pickup" && pricing.shoeBelowMin && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-xs font-medium text-destructive" data-testid="shoe-min-note">
              Home pickups need {pricing.shoeMinPairs}+ pairs for Shoe &amp; Accessories{pricing.shoeWaiverValue > 0 ? <>, or add other items to pass {money(pricing.shoeWaiverValue)} (you&apos;re at {money(pricing.subtotal)})</> : null}. In-store drop-off has no minimum.
            </div>
          )}

          {mode === "pickup" && (
            <div className="rounded-xl border border-border bg-secondary p-3 text-xs text-muted-foreground" data-testid="pickup-charge-note">
              A pickup &amp; delivery charge of <span className="font-semibold text-foreground">{money(pricing.deliveryFee || pricing.pickupBaseFee)}</span><InfoDot testId="info-pickup-fee" label="Pickup & delivery charge" text={copy(shop, "copy_fee_pickup_info", "A flat pickup & delivery charge applies to home pickups: a base fee up to a bill threshold, then a small step fee per slice of bill above it. It scales with your order value.")} /> applies to home pickups (it scales with your order value){pricing.pickupMinOrder > 0 ? <>, with a minimum order of <span className="font-semibold text-foreground">{money(pricing.pickupMinOrder)}</span> after discounts</> : null}. Distance charges may add on top.
            </div>
          )}

          {mode === "pickup" && (
            <div className="space-y-3">
              <label className="text-sm font-semibold">Pickup address</label>
              {addresses.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {addresses.map((a) => (
                    <Chip key={a.id} active={address === a.address} onClick={() => { setAddress(a.address); if (a.lat) setLocation({ lat: a.lat, lng: a.lng }); }}>{a.label}</Chip>
                  ))}
                </div>
              )}
              <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} placeholder="Flat / house, street, area, landmark" data-testid="booking-address-input" className="w-full rounded-xl border border-border bg-card p-3 text-sm outline-none focus:border-primary" />
              <div className="flex items-center gap-3">
                <button onClick={useMyLocation} disabled={locating} data-testid="booking-use-location" className="inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary disabled:opacity-60">
                  {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />} Use my location
                </button>
                {location ? <span className="text-xs text-[hsl(var(--success))]">Pinned{pricing.distanceKm != null ? ` · ${pricing.distanceKm} km` : ""}</span> : null}
              </div>
              {pricing.pickupTooFar ? <p className="text-xs text-destructive">Pickup isn&apos;t available over {pricing.pickupMaxKm} km — switch to Drop-off.</p> : null}
              {address.trim().length >= 10 && !addresses.some((a) => a.address === address.trim()) ? (
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  Save as:
                  {ADDRESS_LABELS.map((l) => (
                    <button key={l} onClick={() => { saveAddress(l, address, location); toast.success(`Saved as ${l}`); }} className="rounded-full bg-secondary px-3 py-1 font-medium text-foreground">{l}</button>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          {pricing.dryWashSubtotal > 0 && (
            <div className="space-y-3">
              <label className="text-sm font-semibold">Delivery speed</label>
              <p className="text-xs text-muted-foreground">Applies to Dry Clean &amp; Wash n Fold items. Tailoring keeps its own turnaround below.</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {["standard", "priority", "express", "rapid"].map((sp) => {
                  const fee = speedFee(sp);
                  return (
                    <button key={sp} data-testid={`booking-speed-${sp}`} onClick={() => setDeliverySpeed(sp)} className={cn("flex items-center justify-between rounded-xl border p-3 text-left transition", deliverySpeed === sp ? "border-primary bg-[hsl(var(--brand-tertiary))]" : "border-border bg-card")}>
                      <div>
                        <p className="text-sm font-semibold">{SPEED_LABELS[sp]}</p>
                        <p className="text-xs text-muted-foreground">{speedTurnaround(sp)}</p>
                      </div>
                      <span className="text-sm font-semibold text-primary">{fee > 0 ? `+ ${money(fee)}` : "Free"}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {pricing.hasTailoring && (
            <div className="space-y-3">
              <label className="text-sm font-semibold">Tailoring turnaround</label>
              <div className="flex flex-wrap gap-2">
                {(shop?.tailoring_speeds ?? []).map((ts) => (
                  <Chip key={ts.key} active={tailoringSpeed === ts.key} onClick={() => setTailoringSpeed(ts.key)}>{ts.label}{TAILORING_MULT[ts.key] > 1 ? ` · ${TAILORING_MULT[ts.key]}x` : ""}</Chip>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 2: slot */}
      {step === 2 && (
        <div className="space-y-5">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {days.map((d, i) => (
              <button key={i} onClick={() => { setDateIdx(i); setSlot(null); }} data-testid={`booking-date-${i}`} className={cn("flex min-w-[64px] flex-col items-center rounded-xl border px-3 py-2 text-xs transition", dateIdx === i ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card")}>
                <span className="font-semibold">{d.format("ddd")}</span>
                <span className="text-lg font-bold">{d.format("D")}</span>
                <span>{d.format("MMM")}</span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {slots.map((s) => {
              const avail = slotAvailability[s];
              const full = avail?.full;
              return (
                <button key={s} disabled={full} onClick={() => setSlot(s)} data-testid={`booking-slot-${s}`} className={cn("rounded-xl border px-3 py-2.5 text-sm transition", slot === s ? "border-primary bg-[hsl(var(--brand-tertiary))]" : "border-border bg-card", full && "opacity-40")}>
                  <span className="font-medium">{s}</span>
                  {avail ? (
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">
                      {full ? "Full" : `${avail.remaining} left`}{avail.is_early_late && avail.markup_percent ? ` · +${avail.markup_percent}%` : ""}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          {noPickupSlotsThisDay ? (
            <p className="rounded-xl bg-secondary px-4 py-3 text-sm text-muted-foreground" data-testid="no-slots-note">
              No pickup slots are open on this day. Please pick another date above.
            </p>
          ) : null}
          {selectedSlotAvailability?.is_early_late ? <p className="text-xs text-[hsl(var(--warning))]">Early/late slot — needs shop approval and a small surcharge.</p> : null}
        </div>
      )}

      {/* STEP 3: details */}
      {step === 3 && (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-semibold">Full name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="booking-name-input" className="mt-1 w-full rounded-xl border border-border bg-card p-3 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-sm font-semibold">Phone number</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="numeric" data-testid="booking-phone-input" className="mt-1 w-full rounded-xl border border-border bg-card p-3 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-sm font-semibold">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Stains, instructions, preferences…" data-testid="booking-notes-input" className="mt-1 w-full rounded-xl border border-border bg-card p-3 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-sm font-semibold">Garment photos (up to 4)</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl border border-border">
                  <img src={fileUrl(p.path)} alt="garment" className="h-full w-full object-cover" />
                  <button onClick={() => setPhotos((ph) => ph.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              {photos.length < 4 && (
                <label data-testid="booking-add-photo" className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border text-muted-foreground hover:border-primary">
                  {uploadingPhoto ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
                  <span className="text-[10px]">Add</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onUploadPhoto} />
                </label>
              )}
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: review */}
      {step === 4 && (
        <div className="space-y-5">
          <PriceBreakdown pricing={pricing} mode={mode} shop={shop} />

          <div className="rounded-2xl border border-primary/30 bg-[hsl(var(--brand-tertiary))] p-4" data-testid="earn-preview">
            <p className="text-sm font-semibold text-primary">{copy(shop, "copy_earn_preview", "You'll earn ~{points} points when this order is completed.").replace("{points}", pricing.earnedPointsPreview)}</p>
            <p className="mt-1 text-xs text-muted-foreground">{copy(shop, "copy_points_validity_note", "Points are credited when the order completes and stay valid for your account; game and milestone bonuses show their own expiry.")}</p>
          </div>

          {pricing.pickupBelowMin ? (
            <div className="rounded-2xl border border-[hsl(var(--warning))] bg-[hsl(36_100%_50%_/_0.08)] p-4 text-sm" data-testid="pickup-min-warning">
              <p className="font-semibold text-[hsl(32_100%_40%)]">Home pickup needs a minimum of {money(pricing.pickupMinOrder)} after discounts.</p>
              <p className="mt-1 text-muted-foreground">You&apos;re at {money(pricing.discountedGoods)}. Add more items, or switch to in-store drop-off (no minimum).</p>
              <button onClick={() => { setMode("dropoff"); setStep(1); }} className="mt-2 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">Switch to Drop-off</button>
            </div>
          ) : null}

          {!hasValidPickupSlot ? (
            <div className="rounded-2xl bg-secondary p-4 text-sm" data-testid="review-slot-note">
              <p className="font-semibold">Pick a pickup time to confirm.</p>
              <p className="mt-1 text-muted-foreground">No pickup slot is set for your chosen day. Choose a collection time, or pick another date.</p>
              <button onClick={() => setStep(2)} data-testid="review-pick-slot" className="mt-2 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">Choose a time</button>
            </div>
          ) : null}

          {/* Promo & referral */}
          <div className="space-y-3 rounded-2xl border border-border p-4">
            <div className="flex gap-2">
              <input value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder="Promo code" data-testid="booking-promo-input" className="flex-1 rounded-xl border border-border bg-card p-2.5 text-sm outline-none focus:border-primary" />
              <button onClick={() => applyPromo.mutate()} disabled={!promoInput.trim() || applyPromo.isPending} data-testid="booking-promo-apply" className="rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50">Apply</button>
            </div>
            {promo ? <p className="text-xs font-medium text-[hsl(var(--success))]">{promo.code} · {promo.label}</p> : null}
            <div className="flex gap-2">
              <input value={referralInput} onChange={(e) => setReferralInput(e.target.value)} placeholder="Referral code" data-testid="booking-referral-input" className="flex-1 rounded-xl border border-border bg-card p-2.5 text-sm outline-none focus:border-primary" />
              <button onClick={() => applyReferral.mutate()} disabled={!referralInput.trim() || applyReferral.isPending} data-testid="booking-referral-apply" className="rounded-xl border border-primary px-4 text-sm font-semibold text-primary disabled:opacity-50">Apply</button>
            </div>
            {referral ? <p className="text-xs font-medium text-[hsl(var(--success))]">{referral.label}</p> : null}
          </div>

          {/* Redemption toggles */}
          {isSignedIn ? (
            <div className="space-y-1 rounded-2xl border border-border p-4">
              <div className="flex items-center justify-between pb-1" data-testid="rewards-balance">
                <p className="text-sm font-semibold">Rewards</p>
                <p className="text-xs text-muted-foreground">Balance: {loyalty?.points ?? 0} pts{pricing.bonusPointsAvailable > 0 ? ` · ${pricing.bonusPointsAvailable} game` : ""}{pricing.milestonePointsAvailable > 0 ? ` · ${pricing.milestonePointsAvailable} milestone` : ""}</p>
              </div>
              {pricing.regularPoints > 0 ? (
                <>
                  <ToggleRow testId="redeem-points" label={`Redeem loyalty points (${pricing.regularPoints})`} hint={`Save up to ${money(Math.floor(pricing.regularPoints * (loyalty?.redeem_value_per_point ?? 1)))}`} checked={redeemPoints} onChange={(v) => { setRedeemPoints(v); if (!v) setRedeemPointsAmount(null); }} disabled={!pricing.canRedeemPoints} reason={pricing.regularPoints < pricing.pointsRedeemMin ? `Need at least ${pricing.pointsRedeemMin} point(s)` : "Not applicable to the items in this cart"} />
                  {redeemPoints && pricing.canRedeemPoints && pricing.maxRedeemablePoints > 0 ? (
                    <PointsStepper
                      value={pricing.pointsRedeemedCount}
                      min={pricing.pointsRedeemMin}
                      max={pricing.maxRedeemablePoints}
                      valuePerPoint={loyalty?.redeem_value_per_point ?? 1}
                      onChange={setRedeemPointsAmount}
                    />
                  ) : null}
                </>
              ) : null}
              {pricing.hasGamePoints ? (
                pricing.canRedeemGamePoints ? (
                  <ToggleRow testId="redeem-game-points" label={`${copy(shop, "copy_game_points_title", "Use game points")} (${pricing.bonusPointsAvailable})`} hint={`Covers up to ${money(pricing.gamePointsCoverage)} of this bill${loyalty?.bonus_points_expires_at ? ` · expires ${dayjs(loyalty.bonus_points_expires_at).format("D MMM YYYY")}` : ""}`} checked={redeemGamePoints} onChange={setRedeemGamePoints} />
                ) : (
                  <div data-testid="game-points-locked" className="rounded-xl bg-secondary p-3">
                    <p className="flex items-center text-sm font-medium">{copy(shop, "copy_game_points_title", "Use game points")} ({pricing.bonusPointsAvailable})<InfoDot testId="info-game-points" label="Game points" text={copy(shop, "copy_game_points_guidance", "Earn points by playing games in the Games hub, then redeem them here. A minimum order and per-category limits apply.")} /></p>
                    <p className="mt-0.5 text-xs text-muted-foreground" data-testid="game-points-reason">{gamePointsReason}</p>
                    {loyalty?.bonus_points_expires_at ? <p className="mt-0.5 text-xs text-muted-foreground">Expires {dayjs(loyalty.bonus_points_expires_at).format("D MMM YYYY")}</p> : null}
                  </div>
                )
              ) : null}
              {pricing.milestonePointsAvailable > 0 ? <ToggleRow testId="redeem-milestone-points" label={`Use milestone bonus (${pricing.milestonePointsAvailable})`} hint={loyalty?.milestone_points_expires_at ? `Expires ${dayjs(loyalty.milestone_points_expires_at).format("D MMM YYYY")}` : ""} checked={redeemMilestonePoints} onChange={setRedeemMilestonePoints} disabled={!pricing.canRedeemMilestonePoints} reason="Not applicable to the items in this cart" /> : null}
              {loyalty?.rewards_available > 0 ? <ToggleRow testId="use-reward" label="Use free wash reward" hint={pricing.rewardItem ? displayName(pricing.rewardItem) : ""} checked={useReward} onChange={setUseReward} disabled={!pricing.canUseReward} reason="Add a wash item to apply this reward" /> : null}
              {loyalty?.free_wash_kg > 0 ? <ToggleRow testId="use-free-wash" label={`Apply free-kg allowance (${loyalty?.free_wash_kg ?? 0} kg)`} checked={useFreeWash} onChange={setUseFreeWash} disabled={!pricing.canUseFreeWash} reason="Add a per-kg wash to use this" /> : null}
              {pricing.regularPoints <= 0 && !pricing.hasGamePoints && !(pricing.milestonePointsAvailable > 0) && !(loyalty?.rewards_available > 0) && !(loyalty?.free_wash_kg > 0) ? (
                <p className="text-xs text-muted-foreground">No loyalty rewards available for this order yet.</p>
              ) : null}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border p-4" data-testid="guest-redeem-nudge">
              <p className="text-sm text-muted-foreground">{copy(shop, "copy_guest_nudge", "Sign in to redeem loyalty points, game points and rewards on this order.")}</p>
              <button type="button" onClick={signIn} data-testid="guest-signin-button" className="mt-2 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">Sign in</button>
            </div>
          )}

          {/* Ways to earn points */}
          <div className="rounded-2xl border border-border p-4" data-testid="ways-to-earn">
            <p className="text-sm font-semibold">{copy(shop, "copy_earn_ways_intro", "Three ways to collect points:")}</p>
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              <li>• {copy(shop, "copy_earn_ways_spend", "Spending — every order earns points (₹{rate} spent = 1 point).").replace("{rate}", pricing.rupeesPerPoint)}</li>
              <li>• {copy(shop, "copy_earn_ways_games", "Games — play in the Games hub to win bonus points.")}</li>
              <li>• {copy(shop, "copy_earn_ways_share", "Sharing — share your referral code; when a friend's first order completes, you both earn bonus points.")}</li>
            </ul>
            {isSignedIn && loyalty?.referral_code ? <p className="mt-2 text-xs">Your referral code: <span className="font-semibold text-primary" data-testid="my-referral-code">{loyalty.referral_code}</span></p> : null}
          </div>

          {/* Terms */}
          <label className="flex items-start gap-3 rounded-2xl border border-border p-4 text-sm">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} data-testid="booking-terms-checkbox" className="mt-0.5 h-4 w-4 accent-[hsl(var(--brand))]" />
            <span>I accept the <button type="button" onClick={() => setTermsOpen(true)} className="font-semibold text-primary underline">Terms & Care policy</button>.</span>
          </label>
        </div>
      )}

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-30 glass border-t border-border">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          {pricing.itemCount > 0 ? <div className="text-sm"><span className="font-semibold">{pricing.itemCount} items</span> · {money(ctaTotal)}{step === 0 ? <span className="text-muted-foreground"> items only</span> : null}</div> : <div className="text-sm text-muted-foreground">No items yet</div>}
          <button onClick={continueStep} disabled={createBooking.isPending} data-testid="booking-continue-button" className={cn("ml-auto rounded-full px-6 py-3 text-sm font-bold text-primary-foreground transition", canContinue ? "bg-primary" : "bg-muted-foreground/40", createBooking.isPending && "opacity-70")}>
            {createBooking.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : ctaLabel}
          </button>
        </div>
      </div>

      {/* Campaign popup */}
      <Dialog open={!!campaign} onOpenChange={(o) => !o && setCampaign(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[hsl(var(--warning))]" /> {campaign?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{campaign?.message}</p>
          <button onClick={() => { setCampaign(null); triggerCelebrate(); }} data-testid="campaign-claim-button" className="mt-2 w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground">Claim it</button>
        </DialogContent>
      </Dialog>

      {/* Terms modal */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Terms & Care Policy</DialogTitle></DialogHeader>
          <TermsBody shop={shop} />
          <button onClick={() => { setAcceptedTerms(true); setTermsOpen(false); }} className="mt-2 w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground">Accept & close</button>
        </DialogContent>
      </Dialog>

      {/* Celebration */}
      {showCelebrate ? (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
          <div className="animate-sparkle flex flex-col items-center gap-2 rounded-3xl bg-primary px-8 py-6 text-primary-foreground shadow-2xl">
            <Sparkles className="h-10 w-10" />
            <p className="font-display text-lg font-bold">{shop?.celebrate_message || "Discount applied!"}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PointsStepper({ value, min, max, valuePerPoint, onChange }) {
  const clamp = (n) => Math.max(min, Math.min(max, n));
  const set = (n) => onChange(clamp(Number.isFinite(n) ? Math.round(n) : min));
  return (
    <div className="rounded-xl bg-secondary p-3" data-testid="points-stepper">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium">Points to redeem</p>
          <p className="text-xs text-muted-foreground">Saving {money(Math.round(value * valuePerPoint))} · {min}–{max} points</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => set(value - 1)} disabled={value <= min} data-testid="points-stepper-dec" className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card text-lg font-bold leading-none disabled:opacity-40">−</button>
          <input
            type="number"
            inputMode="numeric"
            value={value}
            min={min}
            max={max}
            onChange={(e) => set(parseInt(e.target.value, 10))}
            data-testid="points-stepper-input"
            className="w-16 rounded-lg border border-border bg-card p-2 text-center text-sm font-semibold outline-none focus:border-primary"
          />
          <button type="button" onClick={() => set(value + 1)} disabled={value >= max} data-testid="points-stepper-inc" className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card text-lg font-bold leading-none disabled:opacity-40">+</button>
        </div>
      </div>
      <button type="button" onClick={() => onChange(null)} data-testid="points-stepper-max" className="mt-2 text-xs font-semibold text-primary">Use max ({max})</button>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testId, disabled, reason }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className={cn("text-sm font-medium", disabled && "text-muted-foreground")}>{label}</p>
        {disabled && reason ? <p className="text-xs text-destructive" data-testid={`reason-${testId}`}>{reason}</p> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <Switch checked={checked && !disabled} onCheckedChange={onChange} disabled={disabled} data-testid={`toggle-${testId}`} />
    </div>
  );
}

function PriceBreakdown({ pricing, mode, shop }) {
  const rows = [
    ["Subtotal", pricing.subtotal, false, null],
    pricing.promoDiscount > 0 && ["Promo discount", -pricing.promoDiscount, true, null],
    pricing.earlybirdDiscount > 0 && ["Early-bird discount", -pricing.earlybirdDiscount, true, null],
    pricing.pointsDiscount > 0 && ["Loyalty points", -pricing.pointsDiscount, true, null],
    pricing.gamePointsDiscount > 0 && ["Game points", -pricing.gamePointsDiscount, true, null],
    pricing.milestonePointsDiscount > 0 && ["Milestone bonus", -pricing.milestonePointsDiscount, true, null],
    pricing.rewardDiscount > 0 && ["Free wash reward", -pricing.rewardDiscount, true, null],
    pricing.freeWashDiscount > 0 && ["Free-kg allowance", -pricing.freeWashDiscount, true, null],
    pricing.welcomeDiscount > 0 && ["Welcome offer", -pricing.welcomeDiscount, true, null],
    mode === "pickup" && pricing.deliveryFee > 0 && ["Pickup & delivery", pricing.deliveryFee, false, { label: "Pickup & delivery charge", text: copy(shop, "copy_fee_pickup_info", "A flat pickup & delivery charge applies to home pickups: a base fee up to a bill threshold, then a step fee per slice of bill above it, plus a distance add-on beyond the free radius.") }],
    pricing.expressFee > 0 && ["Fast-service fee", pricing.expressFee, false, { label: "Fast-service fee", text: copy(shop, "copy_fee_fastservice_info", "Priority, Express and Rapid finish faster than standard. The fee is worked out from your Dry Clean & Wash bill.") }],
    pricing.slotSurcharge > 0 && ["Slot surcharge", pricing.slotSurcharge, false, { label: "Early/late slot surcharge", text: copy(shop, "copy_fee_slot_info", "Very early or late time slots carry a surcharge because they need extra staffing. Pick a regular slot to avoid it.") }],
    pricing.weightMinTopup > 0 && ["Minimum-order top-up", pricing.weightMinTopup, false, { label: "Minimum-wash top-up", text: copy(shop, "copy_fee_mintopup_info", "Per-kg wash has a minimum billable weight. If your load is under it, a small top-up makes up the difference.") }],
  ].filter(Boolean);
  return (
    <div className="space-y-2 rounded-2xl bg-secondary p-4" data-testid="price-breakdown">
      {rows.map(([label, amt, green, info], i) => (
        <div key={i} className="flex justify-between text-sm">
          <span className={cn("flex items-center", green ? "text-[hsl(var(--success))]" : "text-foreground/80")}>
            {label}
            {info ? <InfoDot label={info.label} text={info.text} testId={`info-${String(label).toLowerCase().replace(/[^a-z]+/g, "-")}`} /> : null}
          </span>
          <span className={green ? "font-medium text-[hsl(var(--success))]" : ""}>{amt < 0 ? `- ${money(-amt)}` : money(amt)}</span>
        </div>
      ))}
      <div className="flex justify-between border-t border-border pt-2 font-display text-base font-bold">
        <span>Estimated total</span>
        <span>{money(pricing.grandTotal)}</span>
      </div>
      <p className="text-[11px] text-muted-foreground">Final bill confirmed after inspection.</p>
    </div>
  );
}
